import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn } from "lucide-react";

/**
 * ZoomImage — drop-in replacement for <img>. Renders the image inline;
 * on click opens a fullscreen lightbox with pan / zoom. Keyboard: ESC to close,
 * "+" and "-" to zoom, "0" to reset. Click-drag pans when zoomed in.
 */
export const ZoomImage = ({ src, alt, className = "", loading = "lazy", caption, ...rest }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Zoom into: ${alt}`}
        data-testid="zoom-image-trigger"
        className="group relative w-full h-full block cursor-zoom-in overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <img src={src} alt={alt} loading={loading} className={className} {...rest} />
        <span className="pointer-events-none absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/70 backdrop-blur px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-white opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn size={11} /> Click to zoom
        </span>
      </button>
      <Lightbox open={open} onClose={() => setOpen(false)} src={src} alt={alt} caption={caption} />
    </>
  );
};

const Lightbox = ({ open, onClose, src, alt, caption }) => {
  const [scale, setScale] = useState(1);
  const [origin, setOrigin] = useState({ x: 0.5, y: 0.5 });
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setScale(1);
    setOrigin({ x: 0.5, y: 0.5 });
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+" || e.key === "=") setScale((s) => Math.min(4, s + 0.5));
      if (e.key === "-" || e.key === "_") setScale((s) => Math.max(1, s - 0.5));
      if (e.key === "0") { setScale(1); setOrigin({ x: 0.5, y: 0.5 }); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const handleClick = (e) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setOrigin({ x, y });
    setScale((s) => (s >= 2 ? 1 : 2));
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="lightbox"
          data-testid="lightbox-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-sm flex flex-col"
          onClick={onClose}
        >
          <div className="absolute top-4 right-4 z-[101] flex items-center gap-3">
            <div className="rounded-full bg-white/10 backdrop-blur px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-white/70 hidden sm:block">
              Esc close · Click to zoom · +/− scale · 0 reset
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              aria-label="Close"
              data-testid="lightbox-close"
              className="h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <div
            ref={wrapRef}
            className="flex-1 flex items-center justify-center p-4 sm:p-10 overflow-hidden"
            onClick={(e) => { e.stopPropagation(); handleClick(e); }}
            style={{ cursor: scale > 1 ? "zoom-out" : "zoom-in" }}
          >
            <motion.div
              key={src}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-full max-h-full"
            >
              <img
                src={src}
                alt={alt}
                draggable={false}
                className="max-w-full max-h-[85vh] object-contain select-none pointer-events-none"
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: `${origin.x * 100}% ${origin.y * 100}%`,
                  transition: "transform 300ms cubic-bezier(0.22, 1, 0.36, 1)",
                }}
              />
            </motion.div>
          </div>

          {caption && (
            <div className="pb-6 px-6 text-center text-sm text-white/60 max-w-3xl mx-auto" onClick={(e) => e.stopPropagation()}>
              {caption}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default ZoomImage;
